// Device-local "favorite contacts" for the brain (W2-D). The web/Android build
// keeps favorites in synchronous `localStorage` (storage/favorites.ts), which
// bare JSC does not provide — so the brain backs the same set with the native KV
// seam instead. Favorites never leave the device (no server API); the set is
// cached in memory and written through to KV so a toggle takes effect without a
// round-trip (offline-first, prime directive).

import type { KvStore } from '../storage/kv.js';

const FAVORITES_KEY = 'favorites';

export class KvFavorites {
  private cache: string[] | undefined;

  constructor(private readonly kv: KvStore) {}

  /** Hydrate the in-memory snapshot once from KV. */
  async load(): Promise<readonly string[]> {
    if (this.cache === undefined) {
      const stored = await this.kv.get<string[]>(FAVORITES_KEY).catch(() => undefined);
      this.cache = Array.isArray(stored) ? stored.filter((v): v is string => typeof v === 'string') : [];
    }
    return this.cache;
  }

  get(): readonly string[] {
    return this.cache ?? [];
  }

  isFavorite(userId: string): boolean {
    return this.get().includes(userId);
  }

  /** Add/remove; no-op if already in the wanted state. Returns the new set. */
  async set(userId: string, favorite: boolean): Promise<readonly string[]> {
    await this.load();
    const current = this.cache ?? [];
    const has = current.includes(userId);
    if (favorite && !has) this.cache = [...current, userId];
    else if (!favorite && has) this.cache = current.filter((id) => id !== userId);
    else return current;
    await this.kv.put(FAVORITES_KEY, this.cache);
    return this.cache;
  }

  /** Flip and return the new state. */
  async toggle(userId: string): Promise<boolean> {
    const next = !this.isFavorite(userId);
    await this.set(userId, next);
    return next;
  }

  /** Wipe (sign-out must not leave the previous account's stars). */
  async clear(): Promise<void> {
    this.cache = [];
    await this.kv.delete(FAVORITES_KEY).catch(() => undefined);
  }
}
